import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { UVInsertConfig } from '@shared/models/uv-inserts';
import type { ResolvedUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import { resolveUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import {
  buildUvInsertCartLineItem,
  type UvInsertCartLineItem,
} from '@shared/view/uv-inserts/build-cart-line-item';
import {
  applyUvInsertSegmentEligibility,
  filterUvSuggestedMessagingForSegment,
} from '@shared/view/uv-inserts/resolve-configs-for-segment';
import { logError } from '../../utilities/logging.js';
import { readGiftWrapUvInsertsBootstrapPayload } from '../bootstrap';
import { findThemeIndex, seedTextByKey } from '../config-state';
import { cropImagesForCart } from '../cropImagesForCart';

const emptyResolvedUvInserts: ResolvedUvInserts = {
  configs: [],
  templateIds: {},
  suggestedMessaging: [],
};

export type ImageTransform = {
  scale: number;
  x: number;
  y: number;
};

type UvInsertsContextType = {
  /**
   * Populated after mount via bootstrap + resolveUvInserts, with market-segment
   * eligibility applied: configs not eligible for `productSegment` are marked inactive
   * so the option-tree navigation treats them as unavailable.
   */
  resolved: ResolvedUvInserts | null;
  /** Market segment of the product being wrapped (empty/null → WOMENS). Set by GiftWrapContext. */
  productSegment: string | null;
  setProductSegment: (segment: string | null) => void;
  /** Clears flow-only state when the gift wrap modal closes. */
  resetUvInsertFlowState: () => void;
  /** Active config for the current visualiser page — set by UvInsertVisualiserPage on mount. */
  activeConfig: UVInsertConfig | null;
  setActiveConfig: (config: UVInsertConfig | null) => void;
  /** Active face tab index within the visualiser. */
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
  /** Theme (variant) index for themed inserts / predesigned cards. */
  themeIndex: number;
  setThemeIndex: (index: number) => void;
  /**
   * Makes the next visualiser open on the first theme whose title contains `match` (case-insensitive),
   * instead of the first theme. Null clears it. Set by the nav on each navigation.
   */
  setPreselectTheme: (match: string | null) => void;
  /** Text keyed by lineItemPropertyKey (e.g. UVFrontText). */
  textByKey: Record<string, string>;
  setText: (key: string, value: string) => void;
  /** Image file keyed by lineItemPropertyKey (for display only — cart uses UserInputContext). */
  imageByKey: Record<string, File | null>;
  setImageByKey: React.Dispatch<React.SetStateAction<Record<string, File | null>>>;
  /** Pinch/pan transform keyed by lineItemPropertyKey. */
  imageTransformByKey: Record<string, ImageTransform>;
  setImageTransformByKey: React.Dispatch<React.SetStateAction<Record<string, ImageTransform>>>;
  /** Theme chooser panel open state. */
  themeChooserOpen: boolean;
  setThemeChooserOpen: (open: boolean) => void;
  /** Suggestion chooser panel open state. */
  suggestionChooserOpen: boolean;
  setSuggestionChooserOpen: (open: boolean) => void;
  /** Selected suggestion group index. */
  selectedSuggestionGroupIndex: number;
  setSelectedSuggestionGroupIndex: (index: number) => void;
  /**
   * Builds a cart line item from MVVM data for the currently active config, cropping each uploaded
   * photo to its framed box first. Returns null when no config is active or no valid theme variant exists.
   * giftWrapKey and parentTitle are generated at add-to-cart time by the caller.
   */
  buildCartLineItem: (options: {
    giftWrapKey: string;
    parentTitle: string;
    vendor?: string | null;
  }) => Promise<UvInsertCartLineItem | null>;
};

const UvInsertsContext = createContext<UvInsertsContextType | undefined>(undefined);

type UvInsertsContextProviderProps = {
  children: React.ReactNode;
};

/**
 * Resolves UV inserts from Liquid bootstrap once on mount and holds flow state for the inserts UI.
 * Wraps GiftWrapProvider at the app root so GiftWrapContext can read `resolved` (e.g. cart skip rules).
 */
export function UvInsertsContextProvider({ children }: UvInsertsContextProviderProps) {
  const [rawResolved, setRawResolved] = useState<ResolvedUvInserts | null>(null);
  const [productSegment, setProductSegment] = useState<string | null>(null);
  const [activeConfig, setActiveConfig] = useState<UVInsertConfig | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [themeIndex, setThemeIndex] = useState(0);
  const [textByKey, setTextByKey] = useState<Record<string, string>>({});
  const [imageByKey, setImageByKey] = useState<Record<string, File | null>>({});
  const [imageTransformByKey, setImageTransformByKey] = useState<Record<string, ImageTransform>>({});
  const [themeChooserOpen, setThemeChooserOpen] = useState(false);
  const [suggestionChooserOpen, setSuggestionChooserOpen] = useState(false);
  const [selectedSuggestionGroupIndex, setSelectedSuggestionGroupIndex] = useState(0);
  const preselectThemeRef = useRef<string | null>(null);

  const setPreselectTheme = useCallback((match: string | null) => {
    preselectThemeRef.current = match;
  }, []);

  // Resolve UV inserts config once on mount.
  useEffect(() => {
    try {
      setRawResolved(resolveUvInserts({ payload: readGiftWrapUvInsertsBootstrapPayload() }));
    } catch (error) {
      logError({
        label: 'UvInsertsContextProvider',
        error,
        message: 'Failed to resolve UV inserts bootstrap payload.',
        extraData: {},
      });
      setRawResolved(emptyResolvedUvInserts);
    }
  }, []);

  // Apply market-segment eligibility against the product being wrapped. Ineligible
  // configs are marked inactive (not removed) so the existing option-tree visibility
  // and bubble-up navigation collapse them; ineligible suggestion groups are dropped.
  // Personalisation does not use this context, so its UV insert targeting is unaffected.
  const resolved = useMemo<ResolvedUvInserts | null>(() => {
    if (!rawResolved) return null;
    return {
      ...rawResolved,
      configs: applyUvInsertSegmentEligibility(rawResolved.configs, productSegment),
      suggestedMessaging: filterUvSuggestedMessagingForSegment(
        rawResolved.suggestedMessaging,
        productSegment,
      ),
    };
  }, [rawResolved, productSegment]);

  const resetUvInsertFlowState = useCallback(() => {
    setActiveConfig(null);
    setActiveTabIndex(0);
    setThemeIndex(0);
    setTextByKey({});
    setImageByKey({});
    setImageTransformByKey({});
    setThemeChooserOpen(false);
    setSuggestionChooserOpen(false);
    setSelectedSuggestionGroupIndex(0);
  }, []);

  // Reset tab and chooser state when active config changes.
  useEffect(() => {
    const preselectIndex = findThemeIndex(activeConfig?.themes ?? [], preselectThemeRef.current);
    setActiveTabIndex(0);
    setThemeIndex(Math.max(preselectIndex, 0));
    setThemeChooserOpen(false);
    setSuggestionChooserOpen(false);
    setSelectedSuggestionGroupIndex(0);
    setImageByKey({});
    setImageTransformByKey({});
    setTextByKey({});
  }, [activeConfig?.id]);

  // Reset suggestion group index when tab changes.
  useEffect(() => {
    setSelectedSuggestionGroupIndex(0);
  }, [activeTabIndex]);

  // Seed text keys from config faceElements when active config changes.
  useEffect(() => {
    if (!activeConfig) return;
    setTextByKey((prev) => seedTextByKey(activeConfig, prev));
  }, [activeConfig?.id]);

  const setText = useCallback((key: string, value: string) => {
    setTextByKey((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Builds a cart line item from the active MVVM config and current user input state.
   * Uses buildUvInsertCartLineItem from the shared view layer — no window access.
   * Photos are cropped to the visible box first, so the printer gets the framed area, not the original.
   */
  const buildCartLineItem = useCallback(async (options: {
    giftWrapKey: string;
    parentTitle: string;
    vendor?: string | null;
  }): Promise<UvInsertCartLineItem | null> => {
    if (!activeConfig || !resolved) return null;
    const croppedImageByKey = await cropImagesForCart({ config: activeConfig, imageByKey, imageTransformByKey });
    return buildUvInsertCartLineItem({
      config: activeConfig,
      themeIndex,
      templateIds: resolved.templateIds,
      textByKey,
      imageByKey: croppedImageByKey,
      giftWrapKey: options.giftWrapKey,
      parentTitle: options.parentTitle,
      vendor: options.vendor ?? null,
    });
  }, [activeConfig, resolved, themeIndex, textByKey, imageByKey, imageTransformByKey]);

  const value = useMemo<UvInsertsContextType>(() => ({
    resolved,
    productSegment,
    setProductSegment,
    resetUvInsertFlowState,
    activeConfig,
    setActiveConfig,
    activeTabIndex,
    setActiveTabIndex,
    themeIndex,
    setThemeIndex,
    setPreselectTheme,
    textByKey,
    setText,
    imageByKey,
    setImageByKey,
    imageTransformByKey,
    setImageTransformByKey,
    themeChooserOpen,
    setThemeChooserOpen,
    suggestionChooserOpen,
    setSuggestionChooserOpen,
    selectedSuggestionGroupIndex,
    setSelectedSuggestionGroupIndex,
    buildCartLineItem,
  }), [
    resolved,
    productSegment,
    resetUvInsertFlowState,
    activeConfig,
    activeTabIndex,
    themeIndex,
    setPreselectTheme,
    textByKey,
    setText,
    imageByKey,
    imageTransformByKey,
    themeChooserOpen,
    suggestionChooserOpen,
    selectedSuggestionGroupIndex,
    buildCartLineItem,
  ]);

  return (
    <UvInsertsContext.Provider value={value}>
      {children}
    </UvInsertsContext.Provider>
  );
}

/**
 * Returns the UV inserts context. Must be used inside UvInsertsContextProvider.
 * @returns {UvInsertsContextType} The UV inserts context value.
 */
export function useUvInsertsContext(): UvInsertsContextType {
  const ctx = useContext(UvInsertsContext);
  if (ctx === undefined) {
    throw new Error('useUvInsertsContext must be used within UvInsertsContextProvider');
  }
  return ctx;
}
